// pages/CleansedLeadDetail.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Building2,
  User,
  CheckCircle,
  MoreVertical,
  FileText,
  Users,
  ExternalLink,
  Plus,
  PhoneCall,
  Briefcase,
  AlertCircle,
  Save,
  X as CloseIcon,
  Loader,
} from "lucide-react";
import apiService from "../../services/api";
import BookAppointmentModal from "../../components/BookAppointmentModal";
import CreateTaskModal from "../../components/CreateTaskModal";
import LogCallModal from "../../components/LogCallModal";
import SendLeadEmailModal from "../../components/SendLeadEmailModal";
import LeadEmailThread from "../../components/LeadEmailThread";
import { useTheme } from "../../contexts/ThemeContext";

const EditableField = ({
  label,
  field,
  value,
  type = "text",
  options = null,
  readOnly = false,
  onSave,
}) => {
  const { isDarkMode } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const getEditValue = useCallback(
    (val) => {
      if (type === "date" && val) {
        try {
          return new Date(val).toISOString().split("T")[0];
        } catch {
          return val || "";
        }
      }
      return val || "";
    },
    [type],
  );

  const getDisplayValue = useCallback(
    (val) => {
      if (type === "date" && val) {
        try {
          return new Date(val).toLocaleDateString();
        } catch {
          return val;
        }
      }
      return val || "—";
    },
    [type],
  );

  const startEditing = useCallback(() => {
    setLocalValue(getEditValue(value));
    setIsEditing(true);
  }, [value, getEditValue]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setLocalValue("");
  }, []);

  const saveChanges = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onSave(field, localValue);
      setIsEditing(false);
      setLocalValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  }, [field, localValue, onSave, isSaving]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        cancelEditing();
      } else if (e.key === "Enter" && type !== "textarea") {
        e.preventDefault();
        saveChanges();
      } else if (e.key === "Enter" && e.ctrlKey && type === "textarea") {
        e.preventDefault();
        saveChanges();
      }
    },
    [cancelEditing, saveChanges, type],
  );

  useEffect(() => {
    if (isEditing && inputRef.current) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          if (["text", "textarea", "email", "tel", "url"].includes(type)) {
            const len = inputRef.current.value.length;
            inputRef.current.setSelectionRange(len, len);
          }
        }
      });
    }
  }, [isEditing, type]);

  useEffect(() => {
    if (!isEditing) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        cancelEditing();
      }
    };
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, cancelEditing]);

  if (readOnly) {
    return (
      <div>
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <p className={isDarkMode ? "text-white" : "text-gray-900"}>
          {getDisplayValue(value)}
        </p>
      </div>
    );
  }

  return (
    <div className="group" ref={containerRef}>
      {label && (
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        {isEditing ? (
          <div className="flex-1 flex items-center gap-2">
            {options ? (
              <select
                ref={inputRef}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
              >
                <option value="">Select...</option>
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : type === "textarea" ? (
              <textarea
                ref={inputRef}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none resize-none`}
                rows={3}
              />
            ) : type === "date" ? (
              <input
                ref={inputRef}
                type="date"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
              />
            ) : (
              <input
                ref={inputRef}
                type={type}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
              />
            )}
            <button
              onClick={saveChanges}
              disabled={isSaving}
              className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              type="button"
            >
              {isSaving ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
            </button>
            <button
              onClick={cancelEditing}
              className={`p-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors`}
              type="button"
            >
              <CloseIcon
                size={16}
                className={isDarkMode ? "text-white" : "text-gray-900"}
              />
            </button>
          </div>
        ) : (
          <>
            <p
              className={`${isDarkMode ? "text-white" : "text-gray-900"} flex-1`}
            >
              {getDisplayValue(value)}
            </p>
            <button
              onClick={startEditing}
              className={`opacity-0 group-hover:opacity-100 p-1 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded transition-all`}
              type="button"
            >
              <Edit
                size={14}
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const CleansedLeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showBookAppointment, setShowBookAppointment] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showLogCall, setShowLogCall] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [emailConnectionStatus, setEmailConnectionStatus] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);

  const leadStatuses = [
    { key: "unqualified", label: "Unqualified" },
    { key: "lead_pot", label: "Lead Pot" },
    { key: "new", label: "New" },
    { key: "nurture", label: "Nurture" },
    { key: "converted", label: "Converted" },
    { key: "dead", label: "Dead" },
  ];

  useEffect(() => {
    fetchLead();
    fetchNotes();
    checkEmailConnection();
  }, [id]);

  const fetchLead = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCleansedLead(id);
      setLead(response.data);
    } catch (error) {
      console.error("Error fetching cleansed lead:", error);
      navigate("/cleansed-leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await apiService.getCleansedLeadNotes(id);
      setNotes(response.data);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const checkEmailConnection = async () => {
    try {
      const response = await apiService.getEmailConnectionStatus();
      setEmailConnectionStatus(response.data);
    } catch (error) {
      setEmailConnectionStatus({ connected: false });
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this cleansed lead?")) {
      try {
        await apiService.deleteCleansedLead(id);
        navigate("/cleansed-leads");
      } catch (error) {
        console.error("Error deleting cleansed lead:", error);
        alert("Failed to delete cleansed lead");
      }
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;
    try {
      await apiService.createCleansedLeadNote(id, {
        title: newNoteTitle,
        body: newNote,
        isPrivate: isPrivateNote,
      });
      setNewNote("");
      setNewNoteTitle("");
      setIsPrivateNote(false);
      fetchNotes();
    } catch (error) {
      console.error("Error creating note:", error);
    }
  };

  const handleEmailClick = () => {
    if (!emailConnectionStatus?.connected) {
      if (
        window.confirm(
          "You need to connect your email first. Would you like to go to Email Settings?",
        )
      ) {
        navigate("/settings/emails");
      }
      return;
    }
    setShowSendEmail(true);
  };

  const handleFieldSave = useCallback(
    async (field, value) => {
      try {
        await apiService.updateCleansedLead(id, { [field]: value });
        await fetchLead();
      } catch (error) {
        console.error("Failed to update field:", error);
        throw error;
      }
    },
    [id],
  );

  const getLeadStatusColor = (status) => {
    const colors = {
      unqualified: "text-gray-400 bg-gray-400/10 border-gray-400/20",
      lead_pot: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      new: "text-cyan-400 bg-cyan-400/10 border-cyan-400/20",
      nurture: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      converted: "text-green-400 bg-green-400/10 border-green-400/20",
      dead: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  const formatLeadStatus = (status) => {
    if (!status) return "None";
    return status
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const getCurrentLeadStatusIndex = () => {
    if (!lead?.leadStatus) return -1;
    return leadStatuses.findIndex((s) => s.key === lead.leadStatus);
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <Loader
          className={isDarkMode ? "text-white" : "text-gray-900"}
          size={32}
        />
      </div>
    );
  }

  if (!lead) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Cleansed lead not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"} transition-colors`}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={handleEmailClick}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors ${isDarkMode ? "text-white" : "text-gray-900"}`}
                title={
                  !emailConnectionStatus?.connected
                    ? "Connect email first"
                    : "Send email"
                }
              >
                <Mail size={16} />
                <span className="hidden sm:inline">Email</span>
                {!emailConnectionStatus?.connected && (
                  <AlertCircle size={14} className="text-yellow-400" />
                )}
              </button>

              <button
                onClick={() => setShowLogCall(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <PhoneCall size={16} />
                <span className="hidden sm:inline">Log Call</span>
              </button>

              <button
                onClick={() => setShowBookAppointment(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <Calendar size={16} />
                <span className="hidden sm:inline">Schedule</span>
              </button>

              <button
                onClick={() => setShowCreateTask(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <CheckCircle size={16} />
                <span className="hidden sm:inline">Task</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors`}
                >
                  <MoreVertical
                    size={20}
                    className={isDarkMode ? "text-white" : "text-gray-900"}
                  />
                </button>
                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg shadow-xl z-50`}
                  >
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} flex items-center gap-2 text-red-400`}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Lead Header Info */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1
                  className={`text-xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {lead.salutation} {lead.fullName || lead.email}
                </h1>
                {lead.leadStatus && (
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium ${getLeadStatusColor(lead.leadStatus)}`}
                  >
                    {formatLeadStatus(lead.leadStatus)}
                  </span>
                )}
                <span className="px-3 py-1 rounded-lg border text-sm font-medium text-emerald-400 bg-emerald-400/10 border-emerald-400/20">
                  Cleansed
                </span>
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {lead.companyName && (
                  <div className="flex items-center gap-2">
                    <Building2 size={16} />
                    <span>{lead.companyName}</span>
                  </div>
                )}
                {lead.jobTitle && (
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} />
                    <span>{lead.jobTitle}</span>
                  </div>
                )}
                {lead.email && (
                  <a
                    href={`mailto:${lead.email}`}
                    className={`flex items-center gap-2 ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"} transition-colors`}
                  >
                    <Mail size={16} />
                    <span>{lead.email}</span>
                  </a>
                )}
                {lead.phone && (
                  <a
                    href={`tel:${lead.phone}`}
                    className={`flex items-center gap-2 ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"} transition-colors`}
                  >
                    <Phone size={16} />
                    <span>{lead.phone}</span>
                  </a>
                )}
              </div>

              {lead.tags && lead.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {lead.tags.map((tag, index) => (
                    <span
                      key={index}
                      className={`px-2 py-1 ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"} rounded text-xs`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {lead.assignedUser && (
              <div
                className={`flex items-center gap-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"} rounded-lg p-3`}
              >
                <div
                  className={`w-10 h-10 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-300"} flex items-center justify-center`}
                >
                  {lead.assignedUser.profileImageUrl ? (
                    <img
                      src={lead.assignedUser.profileImageUrl}
                      alt={lead.assignedUser.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span
                      className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                    >
                      {lead.assignedUser.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Assigned To
                  </div>
                  <div
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {lead.assignedUser.name}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Lead Status Path */}
          <div
            className={`mt-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"} rounded-lg p-3 sm:p-4`}
          >
            <h3
              className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-3`}
            >
              Lead Status
            </h3>
            <div className="flex items-center justify-between overflow-x-auto pb-1 gap-1">
              {leadStatuses.map((status, index) => {
                const currentIndex = getCurrentLeadStatusIndex();
                const isActive = index === currentIndex;
                const isCompleted = index < currentIndex;
                const isCurrent = index <= currentIndex;
                return (
                  <React.Fragment key={status.key}>
                    <button
                      onClick={() => handleFieldSave("leadStatus", status.key)}
                      className="flex-1 flex flex-col items-center gap-1 sm:gap-2 transition-all cursor-pointer hover:opacity-80 min-w-[40px]"
                    >
                      <div
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                          isActive
                            ? "bg-blue-500 border-blue-500 text-white"
                            : isCompleted
                              ? "bg-green-500 border-green-500 text-white"
                              : isDarkMode
                                ? "bg-gray-700 border-gray-600 text-gray-400"
                                : "bg-gray-200 border-gray-300 text-gray-500"
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle size={20} />
                        ) : (
                          <span className="text-sm font-semibold">
                            {index + 1}
                          </span>
                        )}
                      </div>
                      <div className="text-center hidden sm:block">
                        <div
                          className={`text-xs font-medium ${isCurrent ? (isDarkMode ? "text-white" : "text-gray-900") : isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {status.label}
                        </div>
                      </div>
                    </button>
                    {index < leadStatuses.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 transition-all ${index < currentIndex ? "bg-green-500" : isDarkMode ? "bg-gray-700" : "bg-gray-300"}`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div
              className={`mt-3 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} text-center`}
            >
              Click on a status to update the lead's progress
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border`}
            >
              <div
                className={`flex border-b overflow-x-auto ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                <button
                  onClick={() => setActiveTab("details")}
                  className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === "details"
                      ? isDarkMode
                        ? "text-blue-400 border-b-2 border-blue-400"
                        : "text-blue-600 border-b-2 border-blue-600"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FileText size={16} />
                    Details
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("emails")}
                  className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === "emails"
                      ? isDarkMode
                        ? "text-blue-400 border-b-2 border-blue-400"
                        : "text-blue-600 border-b-2 border-blue-600"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Mail size={16} />
                    Emails
                  </div>
                </button>
              </div>

              <div className="p-4 sm:p-6">
                {activeTab === "details" && (
                  <div className="space-y-6">
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <User size={18} />
                        Lead Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Lead Owner"
                          field="assignedTo"
                          value={lead.assignedUser?.name || "Unassigned"}
                          readOnly
                        />
                        <EditableField
                          label="Name"
                          field="fullName"
                          value={lead.fullName}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Company"
                          field="companyName"
                          value={lead.companyName}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Title"
                          field="jobTitle"
                          value={lead.jobTitle}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Lead Source"
                          field="leadSource"
                          value={lead.leadSource}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Lead Status"
                          field="leadStatus"
                          value={lead.leadStatus}
                          options={leadStatuses.map((s) => ({
                            value: s.key,
                            label: s.label,
                          }))}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Industry"
                          field="companyIndustry"
                          value={lead.companyIndustry}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Annual Revenue"
                          field="annualRevenue"
                          value={lead.annualRevenue}
                          type="number"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Rating"
                          field="rating"
                          value={lead.rating}
                          options={[
                            { value: "Hot", label: "Hot" },
                            { value: "Warm", label: "Warm" },
                            { value: "Cold", label: "Cold" },
                          ]}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Phone"
                          field="phone"
                          value={lead.phone}
                          type="tel"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Mobile"
                          field="mobilePhone"
                          value={lead.mobilePhone}
                          type="tel"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Fax"
                          field="fax"
                          value={lead.fax}
                          type="tel"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Email"
                          field="email"
                          value={lead.email}
                          type="email"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Website"
                          field="companyWebsite"
                          value={lead.companyWebsite}
                          type="url"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="No. of Employees"
                          field="numberOfEmployees"
                          value={lead.numberOfEmployees}
                          type="number"
                          onSave={handleFieldSave}
                        />
                        <div className="col-span-1 sm:col-span-2">
                          <EditableField
                            label="Lead Description"
                            field="description"
                            value={lead.description}
                            type="textarea"
                            onSave={handleFieldSave}
                          />
                        </div>
                        <div className="col-span-1 sm:col-span-2">
                          <EditableField
                            label="Caller Notes"
                            field="callerNotes"
                            value={lead.callerNotes}
                            type="textarea"
                            onSave={handleFieldSave}
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <MapPin size={18} />
                        Address Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Street"
                          field="street"
                          value={lead.street}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="City"
                          field="city"
                          value={lead.city}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="State/Province"
                          field="state"
                          value={lead.state}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Zip/Postal Code"
                          field="postalCode"
                          value={lead.postalCode}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Country"
                          field="country"
                          value={lead.country}
                          onSave={handleFieldSave}
                        />
                      </div>
                    </div>

                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created By
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {lead.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(lead.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified By
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {lead.lastModifier?.name ||
                              lead.creator?.name ||
                              "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(lead.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "emails" && (
                  <div
                    className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg p-4 sm:p-6`}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h2
                        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Emails
                      </h2>
                      <button
                        onClick={() => setShowSendEmail(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Send Email
                      </button>
                    </div>
                    <LeadEmailThread leadId={id} leadData={lead} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Notes ({notes.length})
                </h3>
              </div>
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Note title (optional)"
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  className={`w-full px-3 py-2 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg mb-2 focus:outline-none focus:border-blue-500`}
                />
                <textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg resize-none focus:outline-none focus:border-blue-500`}
                />
                <div className="flex items-center justify-between mt-2">
                  <label
                    className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPrivateNote}
                      onChange={(e) => setIsPrivateNote(e.target.checked)}
                      className="rounded"
                    />
                    Private
                  </label>
                  <button
                    onClick={handleCreateNote}
                    disabled={!newNote.trim()}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Note
                  </button>
                </div>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className={`p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"} rounded-lg`}
                  >
                    {note.title && (
                      <div
                        className={`font-medium text-sm mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {note.title}
                      </div>
                    )}
                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"} whitespace-pre-wrap`}
                    >
                      {note.body}
                    </p>
                    <div
                      className={`flex items-center justify-between mt-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <span>{note.creator?.name}</span>
                      <span>
                        {new Date(note.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
                {notes.length === 0 && (
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} text-center py-4`}
                  >
                    No notes yet
                  </p>
                )}
              </div>
            </div>

            {lead.companyName && (
              <div
                className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
              >
                <h3
                  className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Company Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Company Name
                    </label>
                    <p
                      className={`${isDarkMode ? "text-white" : "text-gray-900"} font-medium`}
                    >
                      {lead.companyName}
                    </p>
                  </div>
                  {lead.companyWebsite && (
                    <div>
                      <label
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Website
                      </label>
                      <a
                        href={lead.companyWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      >
                        {lead.companyWebsite}
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  )}
                  {lead.companyIndustry && (
                    <div>
                      <label
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Industry
                      </label>
                      <p
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {lead.companyIndustry}
                      </p>
                    </div>
                  )}
                  {lead.numberOfEmployees && (
                    <div>
                      <label
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Employees
                      </label>
                      <p
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {lead.numberOfEmployees.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showBookAppointment && (
        <BookAppointmentModal
          lead={lead}
          onClose={() => setShowBookAppointment(false)}
          onSuccess={() => setShowBookAppointment(false)}
        />
      )}

      {showCreateTask && (
        <CreateTaskModal
          lead={lead}
          onClose={() => setShowCreateTask(false)}
          onSuccess={() => setShowCreateTask(false)}
        />
      )}

      {showSendEmail && (
        <SendLeadEmailModal
          leadData={lead}
          onClose={() => setShowSendEmail(false)}
          onSuccess={() => {
            setShowSendEmail(false);
            fetchLead();
          }}
        />
      )}

      {showLogCall && (
        <LogCallModal
          lead={lead}
          onClose={() => setShowLogCall(false)}
          onSuccess={() => {
            setShowLogCall(false);
            fetchLead();
          }}
        />
      )}
    </div>
  );
};

export default CleansedLeadDetail;
